import { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  Component,
  afterNextRender,
  inject,
  input,
  signal,
  viewChild,
} from '@angular/core';
import { Router } from '@angular/router';

import type { LoginPasswordRequest, LoginResult, OtpVerifyRequest } from '@core/auth/auth.models';
import { AuthService } from '@core/auth/auth.service';
import { APP_CONFIG } from '@core/config/app-config';
import { ThemeToggle } from '@shared/theme-toggle/theme-toggle';
import { OTP_LENGTH } from '@shared/validators/otp.validator';

import { loginErrorMessage, retryAfterSeconds, throttleMessage } from './login-error.util';
import { LOGIN_COPY, LOGIN_METHOD_TILES, type LoginMethod } from './login.const';
import { OtpLoginForm, type OtpChallengeState } from './otp-login-form/otp-login-form';
import { PasswordLoginForm } from './password-login-form/password-login-form';

/**
 * The login island.
 *
 * THE ONLY EAGER COMPONENT IN THE APP, and deliberately so. esbuild puts a
 * module in the initial bundle exactly when it is reachable by a static import
 * chain from main.ts, and Angular emits no modulepreload hint for lazy route
 * chunks - so a lazy login would cost the browser a full serial round trip of
 * blank screen on a plant network before first meaningful paint. A few kB of
 * never-executed login code for an already-signed-in user is an order of
 * magnitude cheaper than that.
 *
 * It therefore imports NOTHING from @layout or another feature, with one
 * sanctioned exception: the dynamic `import('@layout/shell/shell')` below,
 * which is a runtime prefetch and creates no static edge.
 */
@Component({
  selector: 'app-login',
  imports: [ThemeToggle, PasswordLoginForm, OtpLoginForm],
  templateUrl: './login.html',
  styleUrl: './login.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Login {
  private readonly auth = inject(AuthService);
  private readonly router = inject(Router);

  /**
   * Bound from `?returnUrl=` by `withComponentInputBinding()`.
   *
   * Typed `string | undefined` deliberately. The initial value below is NOT a guarantee:
   * router input binding writes `undefined` for a query parameter that is not present,
   * so declaring this as plain `string` would let the compiler vouch for a value that is
   * routinely undefined at runtime. Keeping `undefined` in the type is what forces every
   * reader to guard.
   */
  readonly returnUrl = input<string | undefined>('');

  // -- Static content (never fetched - see login.const.ts) -----------------

  protected readonly copy = LOGIN_COPY;
  protected readonly tiles = LOGIN_METHOD_TILES;
  protected readonly release = APP_CONFIG.release;

  // -- State ---------------------------------------------------------------

  protected readonly method = signal<LoginMethod>('password');

  protected readonly passwordBusy = signal(false);
  protected readonly passwordError = signal('');

  protected readonly otpSending = signal(false);
  protected readonly otpVerifying = signal(false);
  protected readonly otpError = signal('');
  protected readonly otpNotice = signal('');
  protected readonly otpChallenge = signal<OtpChallengeState | null>(null);

  /** Distinguishes "throttled before any code was sent" from "throttled on a resend". */
  private codeEverSent = false;
  private challengeToken = 0;

  private readonly passwordForm = viewChild(PasswordLoginForm);
  private readonly otpForm = viewChild(OtpLoginForm);

  constructor() {
    // Warm the shell chunk once the login screen has painted, so its fetch
    // overlaps with the seconds the user spends typing instead of stalling
    // the first navigation. afterNextRender, not a constructor call: fetching
    // before paint would compete with the render this route exists to make
    // fast.
    afterNextRender(() => {
      void import('@layout/shell/shell');
    });
  }

  // -- Method switcher -----------------------------------------------------

  /**
   * Switches between the two METHODS. These are not roles: role selection
   * happens after sign-in, in the header dropdown, from the set the server
   * says the user actually holds.
   */
  protected selectMethod(method: LoginMethod): void {
    if (this.method() === method) return;
    this.method.set(method);
    // A stale "credentials rejected" over a freshly swapped field group reads
    // as if the new method already failed.
    this.passwordError.set('');
    this.otpError.set('');
  }

  protected onTileKeydown(event: KeyboardEvent, index: number): void {
    // Arrow keys move between radios, which is what role="radio" promises.
    const delta = event.key === 'ArrowRight' || event.key === 'ArrowDown' ? 1 : -1;
    if (Math.abs(delta) !== 1) return;
    if (!['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp'].includes(event.key)) return;

    event.preventDefault();
    const next = (index + delta + this.tiles.length) % this.tiles.length;
    const tile = this.tiles[next];
    if (tile) this.selectMethod(tile.id);
  }

  // -- Password flow -------------------------------------------------------

  protected onPasswordSubmit(request: LoginPasswordRequest): void {
    if (this.passwordBusy()) return;

    this.passwordBusy.set(true);
    this.passwordError.set('');

    this.auth.loginWithPassword(request).subscribe({
      next: (result) => {
        this.passwordBusy.set(false);
        void this.afterLogin(result);
      },
      error: (error: unknown) => {
        this.passwordBusy.set(false);
        // Inline, never a toast: this is the message the user is reading.
        this.passwordError.set(loginErrorMessage(error));
        this.passwordForm()?.resetPassword();
      },
    });
  }

  protected onForgotPassword(): void {
    // No reset flow is in scope for this package. Say so rather than link to a
    // route that does not exist: OtpPurpose.resetPassword is defined in
    // SHARED_ENUMS but the reset screens are not in the requirements yet.
    this.passwordError.set(
      'Password resets are handled by your site administrator. Contact them to have yours reset.',
    );
  }

  // -- OTP flow ------------------------------------------------------------

  protected onRequestOtp(mobileNumber: string): void {
    if (this.otpSending()) return;

    this.otpSending.set(true);
    this.otpError.set('');

    this.auth.requestOtp({ mobileNumber, purpose: 'login' }).subscribe({
      next: (result) => {
        this.otpSending.set(false);
        this.codeEverSent = true;
        this.otpChallenge.set({
          token: ++this.challengeToken,
          resendAfterSeconds: result.resendAfterSeconds || APP_CONFIG.otp.resendCooldownSeconds,
          expiresInSeconds: result.expiresInSeconds || APP_CONFIG.otp.ttlSeconds,
          canEnterCode: true,
        });
        // Conditional on purpose. An unknown number returns this identical
        // body, so anything more definite would turn the screen into a
        // user-enumeration oracle.
        this.otpNotice.set(
          `If that number is registered, a ${OTP_LENGTH}-digit code has been sent to it.`,
        );
      },
      error: (error: unknown) => {
        this.otpSending.set(false);
        this.onOtpRequestError(error);
      },
    });
  }

  protected onOtpSubmit(request: OtpVerifyRequest): void {
    if (this.otpVerifying()) return;

    this.otpVerifying.set(true);
    this.otpError.set('');

    this.auth.loginWithOtp(request).subscribe({
      next: (result) => {
        this.otpVerifying.set(false);
        void this.afterLogin(result);
      },
      error: (error: unknown) => {
        this.otpVerifying.set(false);

        if (error instanceof HttpErrorResponse && error.status === 429) {
          // Attempts exceeded. The server owns the lockout wording.
          this.otpError.set(throttleMessage(error));
        } else {
          // A wrong code comes back as HTTP 200 / success: false, and the
          // server's message carries the remaining-attempts count that only it
          // can know.
          this.otpError.set(loginErrorMessage(error));
        }

        // Empty the boxes so the next attempt is a fresh six digits rather
        // than an edit of the rejected ones.
        this.otpForm()?.clearDigits();
      },
    });
  }

  private onOtpRequestError(error: unknown): void {
    this.otpNotice.set('');

    if (error instanceof HttpErrorResponse && error.status === 429) {
      const wait = retryAfterSeconds(error) ?? APP_CONFIG.otp.resendCooldownSeconds;
      // A throttled RESEND leaves the previous code alive, so the boxes stay
      // usable and the existing expiry keeps running. A throttled FIRST send
      // has no code to type, so there is nothing to show.
      const remainingExpiry = this.codeEverSent ? (this.otpForm()?.expiryCountdown() ?? 0) : 0;

      this.otpChallenge.set({
        token: ++this.challengeToken,
        resendAfterSeconds: wait,
        expiresInSeconds: remainingExpiry,
        canEnterCode: this.codeEverSent,
      });
      this.otpError.set(throttleMessage(error));
      return;
    }

    this.otpError.set(loginErrorMessage(error));
  }

  // -- After sign-in -------------------------------------------------------

  private afterLogin(result: LoginResult): Promise<boolean> {
    if (result.mustChangePassword) {
      // The seeded Administrator ships with MustChangePassword = true, so this
      // is the first screen the very first user of the system ever sees.
      return this.router.navigate(['/change-password']);
    }
    return this.router.navigateByUrl(this.safeReturnUrl());
  }

  /**
   * `returnUrl` comes from the query string, i.e. from anything that can craft
   * a link. Only same-origin paths are honoured: a protocol-relative
   * `//evil.example` or an absolute URL would otherwise make this login screen
   * an open redirect that arrives with a fresh session.
   */
  private safeReturnUrl(): string {
    // The falsy guard is load-bearing, not defensive noise. `returnUrl` is declared
    // input('') so its type is `string`, but withComponentInputBinding() WRITES
    // `undefined` when the query parameter is absent - which overrides that default.
    // TypeScript therefore sees a non-nullable string and never flags the calls below,
    // while signing in from a plain /login (no ?returnUrl=) throws
    // "Cannot read properties of undefined (reading 'startsWith')" AFTER the credentials
    // have already been accepted. Covered by specs.
    const candidate = this.returnUrl()?.trim();
    if (!candidate) return '/app';
    if (!candidate.startsWith('/')) return '/app';
    if (candidate.startsWith('//')) return '/app';
    if (candidate.startsWith('/login')) return '/app';
    // A scheme or a backslash escape cannot appear in a path we would accept.
    if (/[\\:]/.test(candidate)) return '/app';
    return candidate;
  }
}
